import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/form/PasswordInput";
import { PasswordStrengthMeter } from "@/components/form/PasswordStrengthMeter";
import { FieldError } from "@/components/form/FieldError";
import { SubmitButton } from "@/components/form/SubmitButton";
import { authApi } from "@/features/auth/api";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";

const schema = z
    .object({
        password: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(/[A-Z]/, "Add at least one uppercase letter")
            .regex(/[a-z]/, "Add at least one lowercase letter")
            .regex(/[0-9]/, "Add at least one number"),
        confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((v) => v.password === v.confirmPassword, {
        path: ["confirmPassword"],
        message: "Passwords do not match",
    });

type FormValues = z.infer<typeof schema>;

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") ?? "";
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { password: "", confirmPassword: "" },
        mode: "onBlur",
    });

    const password = watch("password");

    if (!token) {
        return (
            <div data-testid="reset-invalid" className="space-y-6 text-center">
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-error/10 text-error">
                    <AlertTriangle className="h-8 w-8" />
                </div>
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
                        Invalid reset link
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        The link is missing or incomplete. Please request a new one.
                    </p>
                </div>
                <SubmitButton
                    type="button"
                    onClick={() => navigate(ROUTES.forgotPassword)}
                    data-testid="reset-invalid-cta"
                >
                    Request new link
                </SubmitButton>
            </div>
        );
    }

    if (done) {
        return (
            <div data-testid="reset-success" className="space-y-6 text-center">
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand/10 text-brand">
                    <CheckCircle2 className="h-8 w-8" />
                </div>
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
                        Password reset
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Your password has been updated. Sign in to continue.
                    </p>
                </div>
                <SubmitButton
                    type="button"
                    onClick={() => navigate(ROUTES.login)}
                    data-testid="reset-goto-login"
                >
                    Sign in
                </SubmitButton>
            </div>
        );
    }

    const onSubmit = async ({ password }: FormValues) => {
        setLoading(true);
        try {
            await authApi.resetPassword(token, password);
            setDone(true);
            toast.success("Password reset — please sign in.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="reset-page" className="space-y-8">
            <header>
                <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                    Set a new password
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Choose a strong password you haven&apos;t used before.
                </p>
            </header>

            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                aria-label="Reset password form"
                className="space-y-5"
            >
                <div className="space-y-1.5">
                    <Label htmlFor="reset-password">New password</Label>
                    <PasswordInput
                        id="reset-password"
                        placeholder="Create a strong password"
                        autoComplete="new-password"
                        data-testid="reset-password"
                        aria-describedby="reset-password-strength reset-password-error"
                        error={!!errors.password}
                        disabled={loading}
                        {...register("password")}
                    />
                    <PasswordStrengthMeter
                        id="reset-password-strength"
                        password={password ?? ""}
                    />
                    <FieldError
                        id="reset-password-error"
                        message={errors.password?.message}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="reset-confirm">Confirm new password</Label>
                    <PasswordInput
                        id="reset-confirm"
                        placeholder="Re-enter your new password"
                        autoComplete="new-password"
                        data-testid="reset-confirm"
                        aria-describedby={
                            errors.confirmPassword ? "reset-confirm-error" : undefined
                        }
                        error={!!errors.confirmPassword}
                        disabled={loading}
                        {...register("confirmPassword")}
                    />
                    <FieldError
                        id="reset-confirm-error"
                        message={errors.confirmPassword?.message}
                    />
                </div>

                <SubmitButton
                    type="submit"
                    loading={loading}
                    loadingText="Resetting…"
                    data-testid="reset-submit"
                >
                    Reset password
                </SubmitButton>

                <p className="text-center text-sm text-muted-foreground">
                    Remembered your password?{" "}
                    <Link
                        to={ROUTES.login}
                        className="font-semibold text-brand hover:underline"
                    >
                        Sign in
                    </Link>
                </p>
            </form>
        </div>
    );
}
